import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Search, Upload, Shield, Mail, Building, Eye, EyeOff, Download, FileSpreadsheet, Trash2, UserCheck, UserX } from 'lucide-react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({
    email: '', full_name: '', role: 'teacher', department: '', employee_id: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      const response = await api.get(`/users?${params.toString()}`);
      setUsers(response.data.data.users);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Load photo blobs for users who have photo_path
  useEffect(() => {
    users.forEach(async (u) => {
      if (u.photo_path && !photoUrls[u.user_id]) {
        try {
          const token = localStorage.getItem('access_token');
          const resp = await api.get(`/files/${u.photo_path}`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
          const blobUrl = URL.createObjectURL(resp.data);
          setPhotoUrls(prev => ({ ...prev, [u.user_id]: blobUrl }));
        } catch {
          // ignore
        }
      }
    });
  }, [users]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const response = await api.post('/users', form);
      setTempPassword(response.data.data.temporary_password);
      toast.success('Staff member added successfully');
      setForm({ email: '', full_name: '', role: 'teacher', department: '', employee_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  const handlePhotoUpload = async (userId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploadingPhoto(userId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/users/${userId}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/employees/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvResult(res.data.data);
      toast.success(`Imported ${res.data.data.created} staff members`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = (userId, userName) => {
    setConfirm({
      title: 'Delete Staff Member',
      message: `Permanently delete ${userName}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await api.delete(`/users/${userId}`);
          toast.success(`${userName} deleted`);
          fetchUsers();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
      }
    });
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/users/${userId}`, { status: newStatus });
      toast.success(`Staff member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/employees/bulk-template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'staff_template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const roleColors = {
    school_admin: { bg: 'bg-teal-50', text: 'text-teal-600', label: 'Admin' },
    hr: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'HR' },
    teacher: { bg: 'bg-indigo-500/15', text: 'text-indigo-400', label: 'Teacher' },
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center">
            <Users className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Staff Management</h1>
            <p className="text-sm text-slate-500">{users.length} total {users.length === 1 ? 'member' : 'members'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-slate-200 text-slate-600 rounded-xl" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-2" /> Template
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleCsvUpload} />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4" /> {csvUploading ? 'Uploading...' : 'Import CSV'}
            </span>
          </label>
          <Button className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" onClick={() => { setShowAddDialog(true); setTempPassword(''); }} data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Staff
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search staff..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="search-input" />
        </div>
        <div className="flex gap-2">
          {['', 'teacher', 'hr', 'school_admin'].map(r => (
            <Button key={r} size="sm" variant={roleFilter === r ? 'default' : 'outline'}
              className={roleFilter === r ? 'bg-teal-50 text-teal-600 border-teal-500/30 rounded-lg' : 'border-slate-200 text-slate-500 hover:text-white rounded-lg'}
              onClick={() => setRoleFilter(r)} data-testid={`filter-${r || 'all'}`}
            >
              {r ? (r === 'school_admin' ? 'Admin' : r.charAt(0).toUpperCase() + r.slice(1)) : 'All'}
            </Button>
          ))}
        </div>
      </div>

      {/* User Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40 mt-4"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>
      ) : filteredUsers.length === 0 ? (
        <div className="glass-card p-12 mt-4 text-center"><p className="text-slate-500">No staff members found.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="user-grid">
          {filteredUsers.map((u) => {
            const rc = roleColors[u.role] || roleColors.teacher;
            return (
              <div key={u.user_id} className="glass-card p-5 group relative" data-testid={`user-card-${u.user_id}`}>
                <button onClick={() => handleDelete(u.user_id, u.full_name)} className="absolute top-3 right-3 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all" title="Delete staff member">
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
                <div className="flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    {photoUrls[u.user_id] ? (
                      <img src={photoUrls[u.user_id]} alt={u.full_name} className="h-14 w-14 rounded-xl object-cover border border-slate-200" />
                    ) : (
                      <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-teal-500/30 to-emerald-500/20 flex items-center justify-center border border-slate-200">
                        <span className="text-lg font-bold text-teal-600">{u.full_name?.charAt(0) || '?'}</span>
                      </div>
                    )}
                    <label className="absolute -bottom-1 -right-1 h-6 w-6 rounded-lg bg-white/10 hover:bg-teal-500/30 flex items-center justify-center cursor-pointer transition-colors border border-slate-200">
                      <Upload className="h-3 w-3 text-slate-600" />
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(u.user_id, e.target.files[0])} />
                    </label>
                    {uploadingPhoto === u.user_id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
                        <div className="h-5 w-5 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">{u.full_name}</h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Mail className="h-3 w-3 text-slate-400" />
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold ${rc.bg} ${rc.text}`}>
                        <Shield className="h-2.5 w-2.5 mr-1" />{rc.label}
                      </span>
                      {u.department && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-white text-slate-500">
                          <Building className="h-2.5 w-2.5 mr-1" />{u.department}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <button
                    onClick={() => handleToggleStatus(u.user_id, u.status)}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${u.status === 'active' ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'bg-rose-50 text-rose-500 hover:bg-rose-100'}`}
                    title={u.status === 'active' ? 'Click to deactivate' : 'Click to activate'}
                  >
                    {u.status === 'active' ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
                    {u.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                  {u.employee_id && <span className="text-[10px] text-slate-400">ID: {u.employee_id}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1a2332] border-slate-200 text-white max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Add Staff Member</DialogTitle>
            <DialogDescription className="text-slate-500">Create a new user account for your school</DialogDescription>
          </DialogHeader>
          {tempPassword ? (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-sm text-emerald-600 font-medium mb-2">Staff member created successfully!</p>
                <p className="text-xs text-slate-500 mb-3">Share this temporary password securely:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-black/30 text-amber-600 text-sm font-mono">
                    {showPassword ? tempPassword : '••••••••'}
                  </code>
                  <Button size="sm" variant="ghost" className="text-slate-500 hover:text-slate-700" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-teal-600 hover:text-teal-300" onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success('Copied!'); }}>
                    Copy
                  </Button>
                </div>
              </div>
              <Button className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 rounded-xl" onClick={() => setShowAddDialog(false)}>Done</Button>
            </div>
          ) : (
            <form onSubmit={handleAdd} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs">Full Name</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Enter full name" required className="bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="input-fullname" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@school.edu" required className="bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="input-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="select-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="school_admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">Department</Label>
                  <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Science" className="bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="input-department" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs">Employee ID (Optional)</Label>
                <Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} placeholder="e.g. EMP001" className="bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="input-empid" />
              </div>
              <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" disabled={adding} data-testid="submit-add-user">
                {adding ? 'Adding...' : 'Add Staff Member'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV Result Dialog */}
      {csvResult && (
        <Dialog open={!!csvResult} onOpenChange={() => setCsvResult(null)}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Import Results</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-4">
                <div className="flex-1 bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{csvResult.created}</p>
                  <p className="text-xs text-emerald-600">Created</p>
                </div>
                <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{csvResult.skipped}</p>
                  <p className="text-xs text-amber-600">Skipped</p>
                </div>
              </div>
              {csvResult.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {csvResult.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-500">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}
              <Button className="w-full bg-teal-500 text-white rounded-xl" onClick={() => setCsvResult(null)}>Done</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export default UserManagement;
